
import { useState, useEffect, useCallback, useMemo } from 'react';
import { NotificationContext } from '../../contexts/NotificationContext';
import { Client } from '@stomp/stompjs';
import cookieService from "../../utils/CookieService";


const WS_URL = `${process.env.REACT_APP_SOCKET_BASE}`;
const API_BASE_URL = `${process.env.REACT_APP_API_URL}/api`;

const supportsBrowserNotifications = () =>
  typeof window !== "undefined" && "Notification" in window;

const showBrowserNotification = (title, options) => {
  if (!supportsBrowserNotifications()) return;
  if (window.Notification.permission !== "granted") return;
  try {
    new window.Notification(title, options);
  } catch {
    // iOS Safari and some browsers do not support desktop notifications.
  }
};


export const NotificationProvider = ({ children, userId }) => {
  const token = cookieService.getToken();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [stompClient, setStompClient] = useState(null);

  // Authenticated fetch helper
  const authFetch = useCallback(
    (url, options = {}) =>
      fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(options.headers || {}),
        },
      }),
    [token]
  );

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE_URL}/notifications/user/${userId}`);
      if (res.ok) {
        const data = await res.json();
        // Ensure notifications is always an array
        setNotifications(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error('Error fetching notifications:', e);
      setNotifications([]); // Set empty array on error
    }
  }, [authFetch, userId]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE_URL}/notifications/user/${userId}/count`);
      if (res.ok) setUnreadCount(await res.json());
    } catch (e) {
      console.error('Error fetching unread count:', e);
    }
  }, [authFetch, userId]);

  const markAsRead = async (id) => {
    try {
      const res = await authFetch(`${API_BASE_URL}/notifications/${id}/read`, {
        method: 'PUT',
      });
      if (res.ok) {
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, read: true } : n))
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      }
    } catch (e) {
      console.error('Error marking notification as read:', e);
    }
  };

  const markAllAsRead = async () => {
    try {
      const res = await authFetch(`${API_BASE_URL}/notifications/user/${userId}/read-all`, {
        method: 'PUT',
      });
      if (res.ok) {
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        setUnreadCount(0);
      }
    } catch (e) {
      console.error('Error marking all as read:', e);
    }
  };

  // Real-time WebSocket connection using STOMP
  useEffect(() => {
    const activeToken = cookieService.getToken();
    if (!userId || !activeToken) return;

    let client = null;
    let cancelled = false;

    const connect = async () => {
      try {
        const { default: SockJS } = await import("sockjs-client/dist/sockjs");
        if (cancelled) return;

        const sock = new SockJS(WS_URL);
        client = new Client({
          webSocketFactory: () => sock,
          connectHeaders: {
            Authorization: `Bearer ${activeToken}`,
          },
          reconnectDelay: 5000,
          onConnect: () => {
            setIsConnected(true);

            client.subscribe(`/queue/notifications/${userId}`, (msg) => {
              try {
                const notification = JSON.parse(msg.body);
                setNotifications((prev) => [notification, ...prev]);
                setUnreadCount((c) => c + 1);

                showBrowserNotification("New Ticket Update", {
                  body: notification.message,
                  icon: "/notification-icon.png",
                });
              } catch (err) {
                console.error('Error processing incoming message:', err);
              }
            });
          },
          onWebSocketClose: () => {
            setIsConnected(false);
            console.warn('WebSocket closed');
          },
          onStompError: (frame) => {
            console.error('Broker error:', frame.headers['message'], frame.body);
          },
        });

        client.activate();
        setStompClient(client);
      } catch (err) {
        console.error('Failed to initialize notification socket:', err);
      }
    };

    connect();

    return () => {
      cancelled = true;
      if (client) {
        client.deactivate();
      }
      setStompClient(null);
      setIsConnected(false);
    };
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [userId, fetchNotifications, fetchUnreadCount]);

  useEffect(() => {
    if (!supportsBrowserNotifications()) return;
    if (window.Notification.permission !== "default") return;
    window.Notification.requestPermission().catch(() => {});
  }, []);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      isConnected,
      markAsRead,
      markAllAsRead,
      fetchNotifications,
      fetchUnreadCount,
    }),
    [notifications, unreadCount, isConnected]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
